import { CustomButton } from 'holistic-resources';
import {
  useState,
  useEffect,
  createContext,
  useCallback,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import SpeakToYourAgentCardShort from 'components/SpeakToYourAgentCardShort';
import Spinner from 'components/Spinner';
import DefaultHeader from 'components/pageLayout/DefaultHeader';
import Layout from 'components/pageLayout/Layout';
import PageContentContainer from 'components/pageLayout/PageContentContainer';
import SectionTitle from 'components/typography/SectionTitle';
import useWhiteLabelSettings from 'hooks/useWhiteLabelSettings';
import AddressTab from 'pages/profilePage/addressTab/AddressTab';
import ContactTab from 'pages/profilePage/contactTab/ContactTab';
import MarketingTab from 'pages/profilePage/tabs/MarketingTab';
import HolidayPreferencesTab from 'pages/profilePage/tabs/HolidayPreferencesTab';
import PersonalDetailsTab from 'pages/profilePage/tabs/PersonalDetailsTab';
import { getUserProfile } from 'services/ClientService';
import { ReactComponent as ArrowLeft } from '../../images/icons/arrowleft.svg';
import { ReactComponent as ArrowRight } from '../../images/icons/arrownext.svg';

export const ProfileContext = createContext();

export default function ProfilePage() {
  const itemClass = 'mt-[20px] py-2 font-medium text-[22px] flex items-center justify-between text-left';

  const { whiteLabelSettings: { secondaryColour } = {} } =
    useWhiteLabelSettings();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1025);
  const [showTabMenu, setShowTabMenu] = useState(true);
  const { componentConfig } = useSelector(
    (state) => state.ui,
  );

  const doSetProfile = useCallback((data) => {
    setProfile(data);
  }, []);

  const value = useMemo(
    () => ({
      profile,
      doSetProfile,
    }),
    [profile, doSetProfile],
  );

  const tabDefinitions = [
    { label: 'Personal Details', component: <PersonalDetailsTab /> },
    { label: 'Address', component: <AddressTab userData={profile} setUserData={setProfile} /> },
    { label: 'Contact', component: <ContactTab userData={profile} setUserData={setProfile} /> },
    ...(componentConfig && componentConfig.profileMarketingEnabled ? [{ label: 'Marketing', component: <MarketingTab userData={profile} /> }] : []),
    { label: 'Holiday Preferences', component: <HolidayPreferencesTab /> },
  ];

  const getTabBody = () => {
    if (loading) {
      return <Spinner />;
    }

    const activeTabContent = () => {
      return tabDefinitions[activeTabIndex]?.component ?? null;
    };

    return (
      <>
        <div className="mb-4">
          {isMobileView && (
            <CustomButton 
              className={`${itemClass} mt-[0px]`} 
              onClick={() => setShowTabMenu(true)}
            >
              <ArrowLeft className="w-5 mr-4" />
              Back
            </CustomButton>
          )}
        </div>
        {activeTabContent()}
      </>
    );
  };

  useEffect(() => {
    const isMobile = window.innerWidth < 1025;
    setIsMobileView(isMobile);
    setShowTabMenu(true);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const thisProfile = await getUserProfile();
      setProfile(thisProfile);
      setLoading(false);
    })();
  }, []);

  const speakToYourAgentStyles = `
    margin-right: 40px;
    margin-bottom: 40px;
  `;

  return (
    <Layout>
      <DefaultHeader label="Personal Details" />
      <PageContentContainer>
        <div className="flex mt-[40px] mb-[20px] w-full">
          <div className={`flex flex-col border-r ml-8 w-full lg:w-[25%] ${isMobileView && !showTabMenu ? 'hidden' : ''}`}>
            <SectionTitle label="Profile" className="mb-[20px]" />
            <ul>
              {tabDefinitions.map(({ label }, idx) => {
                const isSelected = idx === activeTabIndex;
                const buttonClass = isSelected
                  ? `${itemClass} pl-3 border-l-4`
                  : `${itemClass} pl-4`;
                const buttonStyle = isSelected
                  ? { color: secondaryColour, borderColor: secondaryColour }
                  : {};
                const displayKey = label === 'Marketing' ? 'Marketing Preferences' : label;

                return (
                  <li key={label}>
                    <CustomButton
                      style={buttonStyle}
                      className={buttonClass}
                      onClick={() => {
                        setActiveTabIndex(idx);
                        if (isMobileView) setShowTabMenu(false);
                      }}
                    >
                      {displayKey}
                      {isMobileView && <ArrowRight className="w-5 ml-4" />}
                    </CustomButton>
                  </li>
                );
              })}
            </ul>
            {componentConfig.contactAgentEnabled &&
              <SpeakToYourAgentCardShort styles={speakToYourAgentStyles} />
            }
          </div>
          <div className={`ml-[20px] mr-[15px] mb-[60px] w-full lg:w-[75%] ${isMobileView && showTabMenu ? 'hidden' : 'block'}`}>
            <ProfileContext.Provider value={value}>
              {getTabBody()}
            </ProfileContext.Provider>
          </div>
        </div>
      </PageContentContainer>
    </Layout>
  );
}
